/**
 * @module RecruitingStellen
 * @description Standalone Stellen-Verwaltung — alle offenen Positionen (Fest + Flex)
 *   mit Typ-Filter, Status-Badge, Create-Modal, Edit-Modal, klickbare Bewerber-Counts.
 * @route /recruiting/stellen
 * @since v2.246.0
 * @updated v2.249.0 — Clickable cards, edit modal, bewerber count links
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });
const fj = (url, body, method = 'POST') => f(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const TYP_LABELS = { vollzeit: 'Vollzeit', teilzeit: 'Teilzeit', lehrling: 'Lehrling', praktikum: 'Praktikum', flex: 'Flex', geringfuegig: 'Geringfügig' };
const STELLEN_STATUS = { entwurf: { label: 'Entwurf', color: '#6B7280', bg: '#F3F4F6' }, aktiv: { label: 'Aktiv', color: '#10B981', bg: '#ECFDF5' }, pausiert: { label: 'Pausiert', color: '#F59E0B', bg: '#FEF3C7' }, geschlossen: { label: 'Geschlossen', color: '#EF4444', bg: '#FEF2F2' } };

const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

export default function RecruitingStellen() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stellen, setStellen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typFilter, setTypFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editStelle, setEditStelle] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await f('/api/recruiting/stellen');
      if (res.ok) setStellen(await res.json());
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = stellen.filter(s => {
    if (typFilter && s.typ !== typFilter) return false;
    if (statusFilter && s.status !== statusFilter) return false;
    if (search && !s.titel.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggleStatus = async (e, id, currentStatus) => {
    e.stopPropagation();
    const next = currentStatus === 'aktiv' ? 'pausiert' : 'aktiv';
    await fj(`/api/recruiting/stellen/${id}/status`, { status: next }, 'PUT');
    load();
  };

  const deleteStelle = async (id) => {
    await f(`/api/recruiting/stellen/${id}`, { method: 'DELETE' });
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('recruiting.stellen.title', 'Offene Stellen')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {stellen.filter(s => s.status === 'aktiv').length} {t('recruiting.stellen.aktive', 'aktive Stellen')}
          </p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: '#338edc' }}>
          + {t('recruiting.stellen.neue', 'Neue Stelle')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)]">
          {[
            { key: '', label: t('recruiting.stellen.alle', 'Alle') },
            { key: 'vollzeit', label: 'Fest' },
            { key: 'flex', label: 'Flex' },
          ].map(tb => (
            <button key={tb.key} onClick={() => setTypFilter(tb.key)}
              className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                typFilter === tb.key ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : ''
              }`}
              style={{ color: typFilter === tb.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              {tb.label}
            </button>
          ))}
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border text-xs"
          style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-secondary)' }}>
          <option value="">{t('recruiting.stellen.alleStatus', 'Alle Status')}</option>
          {Object.entries(STELLEN_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t('recruiting.stellen.suche', 'Suche...')}
          className="px-4 py-2 rounded-xl border text-sm"
          style={{ borderColor: 'var(--border-input, #E5E7EB)', color: 'var(--text-primary)' }} />
      </div>

      {/* Stellen list */}
      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('common.loading', 'Laden...')}</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto mb-4" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
          </svg>
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            {t('recruiting.stellen.keine', 'Keine Stellen gefunden')}
          </p>
          <p className="text-xs mb-4" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
            {t('recruiting.stellen.keineHinweis', 'Erstellen Sie eine neue Stelle um Bewerber zuzuordnen.')}
          </p>
          <button onClick={() => setShowNew(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#338edc' }}>
            + {t('recruiting.stellen.neue', 'Neue Stelle')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => {
            const st = STELLEN_STATUS[s.status] || STELLEN_STATUS.entwurf;
            const isFlex = s.typ === 'flex';
            return (
              <div key={s.id} onClick={() => setEditStelle(s)}
                className="bg-[var(--bg-card,#fff)] rounded-xl border p-4 cursor-pointer transition-all hover:border-[#338edc]"
                style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>{s.titel}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0"
                        style={{ background: isFlex ? '#ECFEFF' : '#EBF4FD', color: isFlex ? '#0891B2' : '#338edc' }}>
                        {isFlex ? 'Flex' : TYP_LABELS[s.typ] || s.typ}
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: st.bg, color: st.color }}>
                        {st.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                        {s.standort || 'Graz'} · {t('recruiting.stellen.seit', 'Seit')} {formatDate(s.created_at)}
                      </span>
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/recruiting/bewerber?stelle=${s.id}`); }}
                        className="text-xs font-semibold hover:underline" style={{ color: '#338edc' }}>
                        {s.bewerber_count || 0} {t('recruiting.bewerber.title', 'Bewerber')}
                      </button>
                      {isFlex && s.stundenlohn && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>· {s.stundenlohn}</span>
                      )}
                      {!isFlex && s.gehaltsspanne && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>· {s.gehaltsspanne}</span>
                      )}
                    </div>
                    {s.beschreibung && (
                      <p className="text-xs mt-1.5 line-clamp-2" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        {s.beschreibung}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={(e) => toggleStatus(e, s.id, s.status)} className="text-xs font-medium" style={{ color: '#338edc' }}>
                      {s.status === 'aktiv' ? t('recruiting.stellen.pausieren', 'Pausieren') : t('recruiting.stellen.aktivieren', 'Aktivieren')}
                    </button>
                    {parseInt(s.bewerber_count) === 0 && (
                      <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }} className="text-xs font-medium" style={{ color: '#EF4444' }}>
                        {t('common.delete', 'Löschen')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showNew && <StelleModal t={t} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}

      {/* Edit Modal */}
      {editStelle && <StelleModal t={t} stelle={editStelle} onClose={() => setEditStelle(null)} onSaved={() => { setEditStelle(null); load(); }} />}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setDeleteTarget(null)}>
          <div className="bg-[var(--bg-card,#fff)] rounded-2xl border w-full max-w-md mx-4 p-6" style={{ borderColor: 'var(--border-card, #E5E7EB)' }} onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('recruiting.stellen.deleteTitle', 'Stelle löschen')}
            </h3>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('recruiting.stellen.deleteText', '"{name}" wirklich löschen?').replace('{name}', deleteTarget.titel)}
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-[10px] border text-sm font-medium"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button onClick={() => deleteStelle(deleteTarget.id)}
                className="px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
                {t('common.delete', 'Löschen')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stelle Create/Edit Modal ───
function StelleModal({ t, stelle, onClose, onSaved }) {
  const isEdit = !!stelle;
  const [form, setForm] = useState(stelle ? {
    titel: stelle.titel || '',
    typ: stelle.typ || 'vollzeit',
    beschreibung: stelle.beschreibung || '',
    anforderungen: stelle.anforderungen || '',
    benefits: stelle.benefits || '',
    status: stelle.status || 'aktiv',
    standort: stelle.standort || 'Graz',
    gehaltsspanne: stelle.gehaltsspanne || '',
    stundenlohn: stelle.stundenlohn || '',
  } : {
    titel: '', typ: 'vollzeit', beschreibung: '', anforderungen: '', benefits: '',
    status: 'aktiv', standort: 'Graz', gehaltsspanne: '', stundenlohn: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!form.titel.trim()) return;
    setSaving(true);
    const body = { ...form };
    if (body.typ !== 'flex') { delete body.stundenlohn; } else { delete body.gehaltsspanne; }
    try {
      if (isEdit) {
        const res = await fj(`/api/recruiting/stellen/${stelle.id}`, body, 'PUT');
        if (res.ok) onSaved();
      } else {
        const res = await fj('/api/recruiting/stellen', body);
        if (res.ok) onSaved();
      }
    } catch { /* */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        style={{ borderColor: 'var(--border-card, #E5E7EB)' }} onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          {isEdit ? t('recruiting.stellen.bearbeiten', 'Stelle bearbeiten') : t('recruiting.stellen.neue', 'Neue Stelle')}
        </h3>
        <div className="space-y-3">
          <input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))}
            placeholder={`${t('recruiting.stellen.titel', 'Titel')} *`}
            className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          <div className="flex gap-2">
            <select value={form.typ} onChange={e => setForm(p => ({ ...p, typ: e.target.value }))}
              className="px-3 py-2 rounded-lg border text-sm flex-1" style={{ borderColor: 'var(--border-input, #E5E7EB)' }}>
              {Object.entries(TYP_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
              className="px-3 py-2 rounded-lg border text-sm flex-1" style={{ borderColor: 'var(--border-input, #E5E7EB)' }}>
              <option value="entwurf">{t('recruiting.stellen.entwurf', 'Entwurf')}</option>
              <option value="aktiv">{t('recruiting.stellen.aktivStatus', 'Aktiv')}</option>
              <option value="pausiert">{t('recruiting.stellen.pausiert', 'Pausiert')}</option>
            </select>
          </div>
          <input value={form.standort} onChange={e => setForm(p => ({ ...p, standort: e.target.value }))}
            placeholder={t('recruiting.stellen.standort', 'Standort')}
            className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          {form.typ !== 'flex' && (
            <input value={form.gehaltsspanne} onChange={e => setForm(p => ({ ...p, gehaltsspanne: e.target.value }))}
              placeholder={t('recruiting.stellen.gehaltsspanne', 'Gehaltsspanne (z.B. 2.200–2.800€)')}
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          )}
          {form.typ === 'flex' && (
            <input value={form.stundenlohn} onChange={e => setForm(p => ({ ...p, stundenlohn: e.target.value }))}
              placeholder={t('recruiting.stellen.stundenlohn', 'Stundenlohn (z.B. 12€/h)')}
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          )}
          <textarea value={form.beschreibung} onChange={e => setForm(p => ({ ...p, beschreibung: e.target.value }))}
            placeholder={t('recruiting.stellen.beschreibung', 'Beschreibung')} rows={3}
            className="w-full px-3 py-2 rounded-lg border text-sm resize-none" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          <textarea value={form.anforderungen} onChange={e => setForm(p => ({ ...p, anforderungen: e.target.value }))}
            placeholder={t('recruiting.stellen.anforderungen', 'Anforderungen')} rows={2}
            className="w-full px-3 py-2 rounded-lg border text-sm resize-none" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          <textarea value={form.benefits} onChange={e => setForm(p => ({ ...p, benefits: e.target.value }))}
            placeholder={t('recruiting.stellen.benefits', 'Benefits / Wir bieten')} rows={2}
            className="w-full px-3 py-2 rounded-lg border text-sm resize-none" style={{ borderColor: 'var(--border-input, #E5E7EB)' }} />
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 py-2 rounded-lg border text-sm font-medium"
              style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-60" style={{ background: '#338edc' }}>
              {saving ? '...' : t('common.save', 'Speichern')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
